import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    AppBar,
    Backdrop,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Collapse,
    Divider,
    FilledInput,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from '@mui/material';
import { Email, PermIdentity, Phone, Visibility, VisibilityOff } from '@mui/icons-material';
import { StyledEngineProvider } from '@mui/material/styles';
import SwipeableViews from 'react-swipeable-views';
import ReactCountryFlag from 'react-country-flag';

import StyledSelect from '../../../components/input/StyledSelect';
import AuthProcessorButton from '../../../components/auth/AuthProcessorButton';
import { CountryDialIsoFlags, DefactoCountryFlag } from '../../../library/dssc-country-nationality-municipality-codes';

import TabPanel from '../../../components/TabPanel';
import config from '../../../config';
import { authClearError, authLogin, authSetError } from '../../../store/actions/auth-actions';

import loginStyles from './loginStyles.js';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Carousel } from 'react-responsive-carousel';

const Login = (props) => {
    const styles = loginStyles();
    const dispatch = useDispatch();
    const theme = useTheme();
    const search = useLocation().search;

    const [loginType, setLoginType] = useState(config.REACT_APP_DEFAULT_LOGIN_TYPE || 'phone');
    const [username, setUsername] = useState('');
    const [identityNumber, setIdentityNumber] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneCountryCode, setPhoneCountryCode] = useState(
        config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE
            ? config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE +
                  ':' +
                  CountryDialIsoFlags.find((c) => c.isoCode === config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE)?.dialCode
            : CountryDialIsoFlags[0].isoCode + ':' + CountryDialIsoFlags[0].dialCode,
    );
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoginDisabled, setIsLoginDisabled] = useState(true);
    const loginTypes = ['phone', 'email', 'identityNumber'];
    const [loginTypeIndex, setLoginTypeIndex] = useState(
        loginTypes.findIndex((l) => l === (config.REACT_APP_DEFAULT_LOGIN_TYPE || 'phone')),
    );

    const isAuthLoading = useSelector((state) => state.auth.loading);
    const isAuthError = useSelector((state) => state.auth.error);
    const authErrorMessage = useSelector((state) => state.auth.errorMessage);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const authProcessor = searchParams.get('authprocessor');

        if (authProcessor && authProcessor === 'cancelled') {
            dispatch(authSetError('Sign-in cancelled.'));
        } else if (authProcessor && authProcessor === 'rejected') {
            dispatch(authSetError('Sign-in failed.'));
        }
    }, [dispatch, search]);

    useEffect(() => {
        if (
            !isLoginDisabled &&
            (((!username || username.length <= 0) && (!identityNumber || identityNumber.length <= 0) && (!phone || phone.length <= 0)) ||
                !password ||
                password.length <= 0)
        ) {
            setIsLoginDisabled(true);
        } else if (
            isLoginDisabled &&
            ((username && username.length > 0) || (identityNumber && identityNumber.length > 0) || (phone && phone.length > 0)) &&
            password &&
            password.length > 0
        ) {
            setIsLoginDisabled(false);
        }
    }, [username, identityNumber, phone, password, loginType, isLoginDisabled]);

    const loginTabPanel = (loginType) => {
        let usernameComponent;

        switch (loginType) {
            case 'email':
                usernameComponent = (
                    <FormControl fullWidth variant="filled">
                        <InputLabel htmlFor="login-username" style={{ color: theme.palette.specifics.loginPageInputLabelShrink || '' }}>
                            Username
                        </InputLabel>
                        <FilledInput
                            id="login-username"
                            type="email"
                            placeholder="your@mail.com"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            inputProps={{ form: { autocomplete: 'off' } }}
                            style={{
                                backgroundColor: theme.palette.specifics.loginPageInputBackground || '',
                                color: theme.palette.specifics.loginPageInputLabel || '',
                            }}
                        />
                    </FormControl>
                );
                break;
            case 'identityNumber':
                usernameComponent = (
                    <FormControl fullWidth variant="filled">
                        <InputLabel
                            htmlFor="login-identityNumber"
                            style={{ color: theme.palette.specifics.loginPageInputLabelShrink || '' }}
                        >
                            Identity Number
                        </InputLabel>
                        <FilledInput
                            id="login-identityNumber"
                            type="text"
                            placeholder={config.REACT_APP_USER_IDENTITY_NUMBER_PLACEHOLDER || ''}
                            value={identityNumber}
                            onChange={(event) => setIdentityNumber(event.target.value)}
                            inputProps={{ form: { autocomplete: 'off' } }}
                            style={{
                                backgroundColor: theme.palette.specifics.loginPageInputBackground || '',
                                color: theme.palette.specifics.loginPageInputLabel || '',
                            }}
                        />
                    </FormControl>
                );
                break;
            case 'phone':
                usernameComponent = (
                    <FormControl fullWidth variant="filled">
                        <InputLabel htmlFor="login-phone" style={{ color: theme.palette.specifics.loginPageInputLabelShrink || '' }}>
                            Mobile Number
                        </InputLabel>
                        <FilledInput
                            id="login-phone"
                            type="text"
                            // placeholder="Mobile Number"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            inputProps={{ form: { autocomplete: 'off' } }}
                            style={{
                                backgroundColor: theme.palette.specifics.loginPageInputBackground || '',
                                color: theme.palette.specifics.loginPageInputLabel || '',
                            }}
                            startAdornment={
                                <InputAdornment position="start">
                                    <StyledSelect
                                        id="login-phone-countryCode"
                                        options={CountryDialIsoFlags.map((c) => {
                                            return {
                                                value: `${c.isoCode}:${c.dialCode}`,
                                                display: c.dialCode,
                                                listItemIcon:
                                                    c.isoCode.toUpperCase() === 'XX' ? (
                                                        <img
                                                            style={{
                                                                display: 'inline-block',
                                                                width: '20px',
                                                                height: '20px',
                                                                verticalAlign: 'middle',
                                                            }}
                                                            alt={c.isoCode.toUpperCase()}
                                                            src={DefactoCountryFlag('xx')}
                                                        />
                                                    ) : (
                                                        <ReactCountryFlag
                                                            svg
                                                            countryCode={c.isoCode.toUpperCase()}
                                                            style={{ height: '20px', width: '20px' }}
                                                        />
                                                    ),
                                            };
                                        })}
                                        disableUnderline
                                        fullWidth
                                        noLabel
                                        noFormControl
                                        listItemIcon
                                        value={phoneCountryCode}
                                        handleChange={(e) => setPhoneCountryCode(e.target.value)}
                                        noOutline
                                    />
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                );
                break;
            default:
                break;
        }

        return (
            <StyledEngineProvider injectFirst>
                <Box marginBottom="5px">{usernameComponent}</Box>
                <FormControl fullWidth variant="filled">
                    <InputLabel htmlFor="login-password" style={{ color: theme.palette.specifics.loginPageInputLabelShrink || '' }}>
                        Password
                    </InputLabel>
                    <FilledInput
                        id="login-password"
                        type={showPassword ? 'passwordtext' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        inputProps={{ autoComplete: 'new-password' }}
                        style={{
                            backgroundColor: theme.palette.specifics.loginPageInputBackground || '',
                            color: theme.palette.specifics.loginPageInputLabel || '',
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    edge="end"
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>
            </StyledEngineProvider>
        );
    };

    const handleLoginTypeChange = (event, value) => {
        setUsername('');
        setIdentityNumber('');
        setPhone('');
        setPassword('');
        setLoginType(value);
        setLoginTypeIndex(loginTypes.findIndex((l) => l === loginType));
    };

    const handleSwipeLoginChange = (index) => {
        setLoginType(loginTypes[index]);
        setLoginTypeIndex(index);
    };

    const clearAuthError = () => {
        dispatch(authClearError());
    };

    const handleLogin = (event) => {
        event.preventDefault();

        if (isLoginDisabled) {
            return;
        }

        let selectedUsername;

        switch (loginType) {
            case 'phone':
                selectedUsername = phoneCountryCode.split(':')[1] + phone;
                break;
            case 'email':
                selectedUsername = username?.trim()?.toLowerCase();
                break;
            case 'identityNumber':
                selectedUsername = identityNumber;
                break;
            default:
                break;
        }

        dispatch(authLogin(selectedUsername, password));
    };

    const handleClickRegister = () => {
        props.history.push('/register');
    };

    const renderSwipeableTabs = () => {
        const swipeableTabs = [
            <TabPanel parent="loginType" value={loginTypeIndex} key={0} index={0} dir={theme.direction} nobox>
                {loginTabPanel(loginType)}
            </TabPanel>,
            <TabPanel parent="loginType" value={loginTypeIndex} key={1} index={1} dir={theme.direction} nobox>
                {loginTabPanel(loginType)}
            </TabPanel>,
        ];

        if (config.REACT_APP_USER_KYC_URL) {
            swipeableTabs.push(
                <TabPanel parent="loginType" value={loginTypeIndex} key={2} index={2} dir={theme.direction} nobox>
                    {loginTabPanel(loginType)}
                </TabPanel>,
            );
        }

        return (
            <SwipeableViews
                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                index={loginTypeIndex}
                onChangeIndex={handleSwipeLoginChange}
            >
                {swipeableTabs.map((st) => {
                    return st;
                })}
            </SwipeableViews>
        );
    };

    return (
        <div className={styles.root}>
            <StyledEngineProvider injectFirst>
                <Card className={styles.loginCard}>
                    <Box px={3}>
                        <CardHeader
                            title={config.REACT_APP_LOGIN_ACTION_TEXT}
                            subheader={config.REACT_APP_LOGIN_CATCHPHRASE}
                            classes={{ title: styles.loginCardTitle || undefined, subheader: styles.loginCardHeaderSubheader }}
                        />
                        <Divider />
                        <CardContent>
                            <AppBar position="static" color="default">
                                <Tabs
                                    value={loginType}
                                    onChange={handleLoginTypeChange}
                                    indicatorColor={theme.palette.specifics.loginTabIndicatorColorScheme || 'primary'}
                                    textColor={theme.palette.specifics.loginTabIndicatorColorScheme || 'primary'}
                                    variant="fullWidth"
                                    className={styles.loginTabs}
                                >
                                    <Tab
                                        value="phone"
                                        icon={<Phone />}
                                        id="tab-phone"
                                        aria-label="phone"
                                        aria-controls="tabpanel-phone"
                                        className={styles.loginTypeTab}
                                    />
                                    <Tab
                                        value="email"
                                        icon={<Email />}
                                        id="tab-email"
                                        aria-label="email"
                                        aria-controls="tabpanel-email"
                                        className={styles.loginTypeTab}
                                    />
                                    {config.REACT_APP_USER_KYC_URL && (
                                        <Tab
                                            value="identityNumber"
                                            icon={<PermIdentity />}
                                            id="tab-identityNumber"
                                            aria-label="identityNumber"
                                            aria-controls="tabpanel-identityNumber"
                                            className={styles.loginTypeTab}
                                        />
                                    )}
                                </Tabs>
                            </AppBar>
                            <Box mb={1}>
                                <form onSubmit={handleLogin}>
                                    <Box py={3}>
                                        <Collapse in={isAuthError}>
                                            <Box marginBottom="5px">
                                                <Alert
                                                    severity="error"
                                                    onClose={() => {
                                                        clearAuthError();
                                                    }}
                                                >
                                                    {authErrorMessage}
                                                </Alert>
                                            </Box>
                                        </Collapse>
                                        {/* <SwipeableViews
                      axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                      index={loginTypeIndex}
                      onChangeIndex={handleSwipeLoginChange}
                    > */}
                                        {renderSwipeableTabs()}
                                        {/* </SwipeableViews> */}
                                    </Box>
                                    <Box mb={1}>
                                        <Button
                                            fullWidth
                                            size="large"
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            // disabled={isLoginDisabled}
                                        >
                                            Login
                                        </Button>
                                    </Box>
                                    <a
                                        href="/account/forgotpassword"
                                        style={{ color: theme.palette.specifics.loginPageForgotPasswordColor || '' }}
                                    >
                                        Forgot Password?
                                    </a>
                                </form>
                            </Box>
                            {config.REACT_APP_AUTH_PROCESSOR_URL && (
                                <>
                                    <Divider>Or you can</Divider>
                                    {!!!config.REACT_APP_AUTH_PROCESSOR_VALIDATORS ? (
                                        <Box my={1} display="flex" justifyContent="center">
                                            <AuthProcessorButton
                                                processor={config.REACT_APP_AUTH_PROCESSOR}
                                                processorName={config.REACT_APP_AUTH_PROCESSOR_NAME}
                                                mode="login"
                                            />
                                        </Box>
                                    ) : (
                                        <>
                                            {config.REACT_APP_AUTH_PROCESSOR_VALIDATORS.split(',').map((v, index) => {
                                                const validators = v.split('|');
                                                return (
                                                    <Box my={2} display="flex" justifyContent="center" key={index}>
                                                        <AuthProcessorButton
                                                            fullWidth
                                                            processor={validators[3]}
                                                            processorName={validators[2]}
                                                            identityValidator={validators[0].toLowerCase()}
                                                            countryCode={validators[1]}
                                                            mode="login"
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </>
                                    )}
                                </>
                            )}
                        </CardContent>
                        <Divider />
                        <Box px={1} pt={1} pb={2}>
                            <CardActions>
                                <Grid container direction="column">
                                    <Typography color={theme.palette.specifics.loginPageRegisterCatchphraseColor || ''}>
                                        {config.REACT_APP_ASK_REGISTER_CATCHPHRASE}
                                    </Typography>
                                    <Typography color={theme.palette.specifics.loginPageRegisterSubCatchphraseColor || 'secondary'}>
                                        {config.REACT_APP_ASK_REGISTER_SUB_CATCHPHRASE}
                                    </Typography>
                                </Grid>
                                <div className={styles.grow} />
                                <Button variant="contained" color="secondary" onClick={handleClickRegister}>
                                    Register
                                </Button>
                            </CardActions>
                        </Box>
                        <Backdrop className={styles.backdrop} style={{ position: 'absolute' }} open={isAuthLoading}>
                            <CircularProgress color="inherit" />
                        </Backdrop>
                    </Box>
                </Card>
                <div className={styles.carousel}>
                    <Carousel
                        className="carousel"
                        centerMode={true}
                        showArrows={false}
                        showStatus={false}
                        showIndicators={false}
                        centerSlidePercentage={100}
                        showThumbs={false}
                        autoPlay={true}
                        interval={10000}
                        dynamicHeight={false}
                        infiniteLoop={true}
                    >
                        {/* <div className={styles.carouselImage}>
              <img
                alt={config.REACT_APP_TENANT}
                className={styles.carouselImage}
                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad0.jpg`}
              />
            </div> */}
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad1.jpg`}
                            />
                        </div>
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad2.jpg`}
                            />
                        </div>
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad3.jpg`}
                            />
                        </div>
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad4.jpg`}
                            />
                        </div>
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad5.jpg`}
                            />
                        </div>
                        <div className={styles.carouselImage}>
                            <img
                                alt={config.REACT_APP_TENANT}
                                className={styles.carouselImage}
                                src={`./assets/images/login/${config.REACT_APP_TENANT}/ad6.jpg`}
                            />
                        </div>
                    </Carousel>
                </div>
            </StyledEngineProvider>
        </div>
    );
};

export default Login;
